import { BoxGeometry, DoubleSide, Group, LatheGeometry, Mesh, PlaneGeometry, type Texture, Vector2 } from 'three';
import type { DailySpec } from '../track/modifiers';
import type { SceneryPlan } from '../track/scenery';
import { GeoBuilder, trs } from './geo';
import { mix, PAL } from './palette';
import { boardFrameProto } from './scenery/kit';
import type { Terrain } from './terrain';
import { toonMaterial, vertexColorToon } from './toon';

/**
 * Visuals for track modifiers. Phase 1 has the two customization slots with
 * the house poster: the big billboard at the heaviest braking zone and the
 * vintage blimp. Phase 3 feeds them the winner's poster-processed image, and
 * adds the chaos elements here.
 */
export interface ModifierVisuals {
  group: Group;
  update(t: number): void;
}

export function buildModifierVisuals(spec: DailySpec, plan: SceneryPlan, terrain: Terrain, poster: Texture): ModifierVisuals {
  const group = new Group();
  const updates: ((t: number) => void)[] = [];
  for (const inst of spec.modifiers) {
    if (inst.type === 'billboard') group.add(billboard(plan, terrain, poster));
    if (inst.type === 'blimp') {
      const b = blimp(poster);
      group.add(b);
      const { cx, cy } = plan.bounds;
      const r = Math.max(plan.bounds.maxX - plan.bounds.minX, plan.bounds.maxY - plan.bounds.minY) * 0.42;
      updates.push((t) => {
        const a = t * 0.012 + 1.3;
        b.position.set(cx + Math.cos(a) * r, 118 + Math.sin(t * 0.21) * 2, -(cy + Math.sin(a) * r));
        // Nose along the direction of travel (counter-clockwise seen from above).
        b.rotation.set(0, a + Math.PI, Math.sin(t * 0.3) * 0.02);
      });
    }
  }
  return { group, update: (t) => updates.forEach((u) => u(t)) };
}

function billboard(plan: SceneryPlan, terrain: Terrain, poster: Texture): Group {
  const g = new Group();
  const { x, y, rot } = plan.billboard;
  const W = 9;
  const H = 5.6;
  const lift = 1.5;
  const b = new GeoBuilder();
  b.addProto(boardFrameProto(W, H, lift, lift + H + 0.2), trs(0, 0, 0));
  // A little roof strip over the poster.
  const frame = new Mesh(b.build(), vertexColorToon());
  frame.castShadow = true;
  g.add(frame);
  const face = new Mesh(new PlaneGeometry(W, H), toonMaterial(0xffffff, { map: poster }));
  face.rotation.y = Math.PI / 2;
  face.position.set(0.0, lift + H / 2, 0);
  face.receiveShadow = true;
  g.add(face);
  g.position.set(x, terrain.heightAt(x, y), -y);
  g.rotation.y = rot;
  return g;
}

function blimp(poster: Texture): Group {
  const g = new Group();
  const L = 40;
  const R = 6.5;
  const prof: Vector2[] = [];
  for (let i = 0; i <= 14; i++) {
    const u = i / 14;
    const x = (u - 0.5) * L;
    // Blunt nose, tapered tail.
    const shape = Math.sin(Math.PI * Math.pow(u, 0.85));
    prof.push(new Vector2(Math.max(0.001, R * shape), x));
  }
  const b = new GeoBuilder();
  const segs = 16;
  for (let s = 0; s < segs; s++) {
    const lg = new LatheGeometry(prof, 1, (s / segs) * Math.PI * 2, (Math.PI * 2) / segs);
    lg.rotateZ(-Math.PI / 2);
    b.add(lg, s % 2 === 0 ? PAL.red : PAL.cream);
  }
  // Fins.
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2;
    const fin = new PlaneGeometry(7, 4.5);
    fin.translate(-L / 2 + 4, R * 0.55 + 2, 0);
    fin.rotateX(a);
    b.add(fin, PAL.red);
  }
  // Gondola slung underneath.
  b.add(new BoxGeometry(7, 1.9, 2.4), mix('brown', 'terracotta', 0.3), trs(1, -R - 0.9, 0));
  b.add(new BoxGeometry(7.4, 0.35, 2.8), PAL.cream, trs(1, -R + 0.1, 0));
  const body = new Mesh(b.build(), toonMaterial(0xffffff, { vertexColors: true, doubleSide: true }));
  body.castShadow = true;
  g.add(body);
  // Poster panels on both flanks.
  for (const s of [-1, 1]) {
    const p = new Mesh(new PlaneGeometry(16, 10), toonMaterial(0xffffff, { map: poster, side: DoubleSide }));
    p.position.set(0, 0, s * (R + 0.05));
    if (s < 0) p.rotation.y = Math.PI;
    g.add(p);
  }
  return g;
}
